import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import { AlertDialog as BaseAlertDialog } from "@base-ui/react/alert-dialog";
import { DismissIcon } from "../../primitives/Icons/Icons";
import Tabs from "../Tabs/Tabs";
import { sizeOf } from "../../utils/propTypes";
import css from "./Dialog.module.css";

const DialogAlertContext = React.createContext(false);

const DialogRoot = ({
  open,
  defaultOpen,
  onOpenChange,
  onOpenChangeComplete,
  modal = true,
  alert = false,
  children,
  ...rest
}) => {
  const Root = alert ? BaseAlertDialog.Root : BaseDialog.Root;
  const rootProps = alert
    ? { open, defaultOpen, onOpenChange, onOpenChangeComplete, ...rest }
    : {
        open,
        defaultOpen,
        onOpenChange,
        onOpenChangeComplete,
        modal,
        ...rest,
      };

  return (
    <DialogAlertContext.Provider value={alert}>
      <Root {...rootProps}>{children}</Root>
    </DialogAlertContext.Provider>
  );
};

DialogRoot.displayName = "DialogRoot";

DialogRoot.propTypes = {
  open: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  onOpenChange: PropTypes.func,
  onOpenChangeComplete: PropTypes.func,
  modal: PropTypes.oneOf([true, false, "trap-focus"]),
  alert: PropTypes.bool,
  children: PropTypes.node,
};

const DialogTrigger = React.forwardRef(
  ({ className, children, ...rest }, ref) => {
    const alert = React.useContext(DialogAlertContext);
    const Trigger = alert ? BaseAlertDialog.Trigger : BaseDialog.Trigger;

    return (
      <Trigger ref={ref} className={clsx(css.trigger, className)} {...rest}>
        {children}
      </Trigger>
    );
  },
);

DialogTrigger.displayName = "DialogTrigger";

DialogTrigger.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const DialogClose = React.forwardRef(
  ({ className, children, ...rest }, ref) => {
    const alert = React.useContext(DialogAlertContext);
    const Close = alert ? BaseAlertDialog.Close : BaseDialog.Close;

    return (
      <Close
        ref={ref}
        aria-label="Close"
        className={clsx(css.close, className)}
        {...rest}
      >
        {children || <DismissIcon />}
      </Close>
    );
  },
);

DialogClose.displayName = "DialogClose";

DialogClose.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

// Dialog.Popup renders its own Backdrop inside the portal — do NOT also render
// one alongside a Popup, or the scrim doubles up (twice the dim, twice the
// blur). This part exists only for layouts that compose Portal/Popup by hand.
const DialogBackdrop = React.forwardRef(({ className, ...rest }, ref) => {
  const alert = React.useContext(DialogAlertContext);
  const Backdrop = alert ? BaseAlertDialog.Backdrop : BaseDialog.Backdrop;

  return (
    <Backdrop ref={ref} className={clsx(css.backdrop, className)} {...rest} />
  );
});

DialogBackdrop.displayName = "DialogBackdrop";

DialogBackdrop.propTypes = {
  className: PropTypes.string,
};

const DialogPopup = React.forwardRef(
  (
    {
      size = "md",
      align = "center",
      fullScreenOnCompact = false,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const alert = React.useContext(DialogAlertContext);
    const Portal = alert ? BaseAlertDialog.Portal : BaseDialog.Portal;
    const Popup = alert ? BaseAlertDialog.Popup : BaseDialog.Popup;

    return (
      <Portal>
        <DialogBackdrop />
        <Popup
          ref={ref}
          className={clsx(
            css.popup,
            css[size],
            align !== "center" && css[`align-${align}`],
            fullScreenOnCompact && css.fullScreenOnCompact,
            className,
          )}
          {...rest}
        >
          {children}
        </Popup>
      </Portal>
    );
  },
);

DialogPopup.displayName = "DialogPopup";

DialogPopup.propTypes = {
  size: sizeOf("sm", "md", "lg", "xl", "full"),
  align: PropTypes.oneOf(["center", "top"]),
  fullScreenOnCompact: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
};

const DialogTitle = React.forwardRef(
  ({ className, children, ...rest }, ref) => {
    const alert = React.useContext(DialogAlertContext);
    const Title = alert ? BaseAlertDialog.Title : BaseDialog.Title;

    return (
      <Title ref={ref} className={clsx(css.title, className)} {...rest}>
        {children}
      </Title>
    );
  },
);

DialogTitle.displayName = "DialogTitle";

DialogTitle.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const DialogDescription = React.forwardRef(
  ({ className, children, ...rest }, ref) => {
    const alert = React.useContext(DialogAlertContext);
    const Description = alert
      ? BaseAlertDialog.Description
      : BaseDialog.Description;

    return (
      <Description
        ref={ref}
        className={clsx(css.description, className)}
        {...rest}
      >
        {children}
      </Description>
    );
  },
);

DialogDescription.displayName = "DialogDescription";

DialogDescription.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const DialogBody = React.forwardRef(({ className, children, ...rest }, ref) => (
  <div ref={ref} className={clsx(css.body, className)} {...rest}>
    {children}
  </div>
));

DialogBody.displayName = "DialogBody";

DialogBody.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const DialogActions = React.forwardRef(
  ({ start, className, children, ...rest }, ref) => (
    <div ref={ref} className={clsx(css.actions, className)} {...rest}>
      {start && <div className={css.actionsStart}>{start}</div>}
      {children}
    </div>
  ),
);

DialogActions.displayName = "DialogActions";

DialogActions.propTypes = {
  start: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node,
};

const DialogAppBar = React.forwardRef(
  ({ end, className, children, ...rest }, ref) => (
    <header ref={ref} className={clsx(css.appBar, className)} {...rest}>
      <DialogClose className={css.appBarClose} />
      <div className={css.appBarTitles}>{children}</div>
      {end && <div className={css.appBarEnd}>{end}</div>}
    </header>
  ),
);

DialogAppBar.displayName = "DialogAppBar";

DialogAppBar.propTypes = {
  end: PropTypes.node,
  className: PropTypes.string,
  children: PropTypes.node,
};

const DialogTabs = React.forwardRef(({ className, children, ...rest }, ref) => (
  <Tabs.Root ref={ref} className={clsx(css.dialogTabs, className)} {...rest}>
    {children}
  </Tabs.Root>
));

DialogTabs.displayName = "DialogTabs";

DialogTabs.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onValueChange: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node,
};

const DialogTabsList = React.forwardRef(
  ({ className, children, ...rest }, ref) => (
    <Tabs.List
      ref={ref}
      className={clsx(css.dialogTabsList, className)}
      {...rest}
    >
      {children}
    </Tabs.List>
  ),
);

DialogTabsList.displayName = "DialogTabsList";

DialogTabsList.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const Dialog = {
  Root: DialogRoot,
  Trigger: DialogTrigger,
  Close: DialogClose,
  Popup: DialogPopup,
  Title: DialogTitle,
  Description: DialogDescription,
  Backdrop: DialogBackdrop,
  Body: DialogBody,
  Actions: DialogActions,
  AppBar: DialogAppBar,
  Tabs: DialogTabs,
  TabsList: DialogTabsList,
};

export default Dialog;
